import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";

const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  page: "#f8fafc",
  card: "#ffffff",
};

export default function PublicEventBooking() {
  const [params] = useSearchParams();
  const eventId = params.get("eventId") || "";
  const promoterId = params.get("promoterId") || "";
  const promoterCode = params.get("promoterCode") || "";

  const [event, setEvent] = useState(null);
  const [promoter, setPromoter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bookings, setBookings] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [contact, setContact] = useState({ name: "", phone: "", email: "" });
  const [accepted, setAccepted] = useState(false);
  const [creating, setCreating] = useState(false);
  const [ticket, setTicket] = useState(null);
  const functions = useMemo(() => getFunctions(getApp(), "asia-south1"), []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!eventId) {
        setError("Missing event link.");
        setLoading(false);
        return;
      }
      try {
        const eventSnap = await getDoc(doc(db, "create_new_event", eventId));
        if (!eventSnap.exists()) {
          setError("Event not found.");
          setLoading(false);
          return;
        }
        const eventData = { id: eventSnap.id, ...(eventSnap.data() || {}) };
        let promoterData = null;
        if (promoterId) {
          const promoterSnap = await getDoc(doc(db, "create_newe_promoters", promoterId));
          if (promoterSnap.exists()) promoterData = { id: promoterSnap.id, ...(promoterSnap.data() || {}) };
        }
        if (!mounted) return;
        setEvent(eventData);
        setPromoter(promoterData);
        if (promoterData && (promoterData.isActive === false || promoterData.status === "disabled")) {
          setError("This promoter link is currently disabled.");
        }
      } catch (e) {
        console.error(e);
        if (mounted) setError("Could not load this booking link.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [eventId, promoterId]);

  useEffect(() => {
    if (!eventId) return undefined;
    const unsub = onSnapshot(
      query(collection(db, "new_event_booking_recived"), where("eventId", "==", eventId)),
      (snap) => setBookings(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
      (err) => console.error("booking inventory load failed:", err)
    );
    return () => unsub();
  }, [eventId]);

  const tickets = useMemo(
    () => (Array.isArray(event?.tickets) ? event.tickets.filter((t) => t.active !== false) : []),
    [event]
  );
  const ticketSettings = useMemo(() => normalizeTicketSettings(event?.ticketSettings), [event]);
  const salesStatus = useMemo(() => getSalesStatus(ticketSettings, event), [ticketSettings, event]);
  const selectedItems = useMemo(() => {
    return tickets
      .map((t) => {
        const available = ticketAvailability(t, bookings).available;
        const qty = Math.min(Number(quantities[t.id] || 0), available);
        return {
          ticket_id: t.id,
          tier_name: t.name,
          quantity: qty,
          unit_price: Number(t.price || 0),
          total: qty * Number(t.price || 0),
        };
      })
      .filter((x) => x.quantity > 0);
  }, [bookings, quantities, tickets]);
  const subtotal = selectedItems.reduce((sum, item) => sum + item.total, 0);
  const feeSummary = calculateFeeSummary(subtotal, ticketSettings);
  const total = feeSummary.total;
  const selectedQty = selectedItems.reduce((sum, item) => sum + item.quantity, 0);
  const firstShow = firstShowInstance(event?.schedule?.shows);

  async function confirmBooking() {
    setError("");
    if (!selectedItems.length) return setError("Select at least one ticket.");
    const overSold = selectedItems.find((item) => {
      const ticketRow = tickets.find((ticketItem) => ticketItem.id === item.ticket_id);
      return item.quantity > ticketAvailability(ticketRow || { id: item.ticket_id }, bookings).available;
    });
    if (overSold) return setError(`${overSold.tier_name} does not have enough tickets available.`);
    if (!salesStatus.open) return setError(salesStatus.message);
    if (selectedQty > ticketSettings.maxTicketsPerBooking) {
      return setError(`Maximum ${ticketSettings.maxTicketsPerBooking} tickets allowed in one booking.`);
    }
    if (!contact.name.trim()) return setError("Enter full name.");
    if (contact.phone.replace(/\D/g, "").length < 10) return setError("Enter valid mobile number.");
    if (!contact.email.trim()) return setError("Enter email.");
    if (ticketSettings.requireTermsAcceptance && !accepted) return setError("Accept terms to continue.");
    if (promoter && (promoter.isActive === false || promoter.status === "disabled")) {
      return setError("This promoter link is disabled.");
    }

    setCreating(true);
    try {
      await ensureRazorpayLoaded();
      const bookingDraft = {
        contact: {
          name: contact.name.trim(),
          email: contact.email.trim(),
          phone: contact.phone.replace(/\D/g, ""),
        },
        eventDate: firstShow.date || null,
        eventId,
        eventImage: pickImage(event),
        eventTime: firstShow.time || null,
        eventTitle: event?.name || "Event",
        items: selectedItems,
        promoterId: promoter?.id || promoterId || null,
        promoterCode: promoter?.promoterCode || promoterCode || null,
        summary: {
          coupon_code: null,
          discount: 0,
          donation: 0,
          gst_percent: ticketSettings.gstPercent,
          gst_amount: feeSummary.gst,
          gst_18_percent: feeSummary.gst,
          convenience_fee_percent: ticketSettings.convenienceFeePercent,
          convenience_fee: feeSummary.convenienceFee,
          platform_fee: feeSummary.platformFee,
          fees_absorbed_by_organizer: ticketSettings.absorbFees,
          subtotal,
          total_amount: total,
        },
        terms: {
          accepted: ticketSettings.requireTermsAcceptance ? true : !!accepted,
        },
        venue: event?.schedule?.venueDetails || {
          venueName: event?.schedule?.venueName || "Venue details not available",
        },
      };
      const createOrder = httpsCallable(functions, "eventCreateBookingOrder");
      const confirmPayment = httpsCallable(functions, "eventConfirmBookingPayment");
      const orderRes = await createOrder(bookingDraft);
      const order = orderRes.data || {};
      if (!order?.orderId || !order?.keyId) throw new Error("Payment order could not be created.");

      const paymentResult = await openRazorpayCheckout({
        key: order.keyId,
        amount: order.amountPaise,
        currency: order.currency || "INR",
        name: order.name || "ApnaArtist Events",
        description: order.description || event?.name || "Event Booking",
        order_id: order.orderId,
        prefill: {
          name: contact.name.trim(),
          email: contact.email.trim(),
          contact: contact.phone.replace(/\D/g, ""),
        },
      });

      const verifyRes = await confirmPayment({
        orderId: paymentResult.razorpay_order_id,
        paymentId: paymentResult.razorpay_payment_id,
        signature: paymentResult.razorpay_signature,
      });
      const verified = verifyRes.data || {};
      setTicket({
        ...(verified.booking || bookingDraft),
        id: verified.ticketId,
        ticketId: verified.ticketId,
        paymentId: verified.paymentId,
        razorpayOrderId: verified.orderId,
        status: "confirmed",
        created_at: new Date(),
      });
    } catch (e) {
      console.error(e);
      setError(e?.message || "Could not complete payment. Please try again.");
    } finally {
      setCreating(false);
    }
  }

  if (loading) return <Shell><div style={noteBox}>Loading booking page...</div></Shell>;
  if (error && !event) return <Shell><div style={errorBox}>{error}</div></Shell>;
  if (ticket) return <TicketSuccess ticket={ticket} event={event} />;

  return (
    <Shell>
      <div style={hero}>
        {pickImage(event) ? <img src={pickImage(event)} alt={event?.name || "Event"} style={heroImg} /> : null}
        <div style={heroContent}>
          <div style={eyebrow}>M-Ticket booking</div>
          <h1 style={title}>{event?.name || "Event"}</h1>
          <div style={meta}>{firstShow.date || "Date TBA"} {firstShow.time ? `| ${firstShow.time}` : ""}</div>
          <div style={meta}>{event?.schedule?.venueDetails?.venueName || event?.schedule?.venueName || "Venue TBA"}</div>
          {promoter ? <div style={promoterChip}>Promoter: {promoter.fullName}</div> : null}
        </div>
      </div>

      {error ? <div style={errorBox}>{error}</div> : null}

      <div style={grid}>
        <section style={card}>
          <h2 style={sectionTitle}>Select Tickets</h2>
          {!salesStatus.open ? <div style={errorBox}>{salesStatus.message}</div> : null}
          <div style={{ display: "grid", gap: 10 }}>
            {tickets.map((ticketRow) => (
              <div key={ticketRow.id} style={ticketCard}>
                <div>
                  <div style={{ fontWeight: 900 }}>{ticketRow.name}</div>
                  <div style={{ color: COLORS.subtext }}>{formatMoney(ticketRow.price)} per ticket</div>
                  {ticketSettings.showRemainingCount ? (
                    <div style={availabilityLine(ticketAvailability(ticketRow, bookings).available)}>
                      {ticketAvailability(ticketRow, bookings).sold} sold | {ticketAvailability(ticketRow, bookings).available} available
                      {ticketAvailability(ticketRow, bookings).available <= 5 && ticketAvailability(ticketRow, bookings).available > 0 ? " | Book hurry" : ""}
                    </div>
                  ) : null}
                </div>
                <input
                  type="number"
                  min="0"
                  max={ticketAvailability(ticketRow, bookings).available}
                  disabled={ticketAvailability(ticketRow, bookings).available <= 0 || !salesStatus.open}
                  value={quantities[ticketRow.id] || ""}
                  onChange={(e) => {
                    const available = ticketAvailability(ticketRow, bookings).available;
                    const otherQty = Object.entries(quantities).reduce((sum, [id, qty]) => id === ticketRow.id ? sum : sum + Number(qty || 0), 0);
                    const maxForThisTicket = Math.max(0, Math.min(available, ticketSettings.maxTicketsPerBooking - otherQty));
                    const next = Math.max(0, Math.min(Number(e.target.value || 0), maxForThisTicket));
                    setQuantities((p) => ({ ...p, [ticketRow.id]: next || "" }));
                  }}
                  placeholder="0"
                  style={qtyInput}
                />
              </div>
            ))}
            {!tickets.length ? <div style={noteBox}>No active tickets available.</div> : null}
          </div>
        </section>

        <section style={card}>
          <h2 style={sectionTitle}>Customer Details</h2>
          <Input label="Full Name" value={contact.name} onChange={(v) => setContact((p) => ({ ...p, name: v }))} />
          <Input label="Mobile Number" value={contact.phone} onChange={(v) => setContact((p) => ({ ...p, phone: v }))} />
          <Input label="Email" value={contact.email} onChange={(v) => setContact((p) => ({ ...p, email: v }))} />
          {ticketSettings.requireTermsAcceptance ? (
            <label style={termsLine}>
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
              <span>I accept the event terms and refund policy.</span>
            </label>
          ) : null}
          {ticketSettings.cancellationNote ? <div style={hint}>{ticketSettings.cancellationNote}</div> : null}
        </section>

        <aside style={card}>
          <h2 style={sectionTitle}>Order Summary</h2>
          <SummaryRow label="Subtotal" value={formatMoney(subtotal)} />
          {feeSummary.gst > 0 ? <SummaryRow label={`GST ${ticketSettings.gstPercent}%`} value={formatMoney(feeSummary.gst)} /> : null}
          {feeSummary.convenienceFee > 0 ? <SummaryRow label={`Convenience fee ${ticketSettings.convenienceFeePercent}%`} value={formatMoney(feeSummary.convenienceFee)} /> : null}
          {feeSummary.platformFee > 0 ? <SummaryRow label="Platform fee" value={formatMoney(feeSummary.platformFee)} /> : null}
          <SummaryRow label="Total" value={formatMoney(total)} strong />
          <button type="button" style={payBtn} disabled={creating || !tickets.length || !salesStatus.open} onClick={confirmBooking}>
            {creating ? "Creating ticket..." : "Pay & Get QR Ticket"}
          </button>
          <div style={hint}>QR ticket is generated only after successful Razorpay payment verification.</div>
        </aside>
      </div>
    </Shell>
  );
}

function TicketSuccess({ ticket, event }) {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(ticket.ticketId)}`;
  return (
    <Shell>
      <div style={successCard}>
        <div style={successBadge}>Booking Confirmed</div>
        <h1 style={title}>{event?.name || ticket.eventTitle}</h1>
        <img src={qrUrl} alt="Ticket QR" style={qrImg} />
        <div style={ticketId}>{ticket.ticketId}</div>
        <div style={meta}>Show this QR at entry. Ticket is saved as M-Ticket.</div>
        <div style={summaryBox}>
          <SummaryRow label="Payment ID" value={ticket.paymentId} />
          <SummaryRow label="Amount Paid" value={formatMoney(ticket.summary?.total_amount)} strong />
          <SummaryRow label="Customer" value={ticket.contact?.phone || "-"} />
        </div>
        <button type="button" style={payBtn} onClick={() => window.print()}>Print / Save Ticket</button>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return <main style={page}>{children}</main>;
}

function Input({ label, value, onChange }) {
  return (
    <label style={field}>
      <span>{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} style={input} />
    </label>
  );
}

function SummaryRow({ label, value, strong }) {
  return (
    <div style={summaryRow}>
      <span>{label}</span>
      <b style={{ fontSize: strong ? 18 : 14 }}>{value}</b>
    </div>
  );
}

function normalizeTicketSettings(settings = {}) {
  return {
    gstPercent: clampNumber(settings.gstPercent, 0, 100, 18),
    convenienceFeePercent: clampNumber(settings.convenienceFeePercent, 0, 100, 0),
    platformFeeFlat: Math.max(0, Number(settings.platformFeeFlat || 0)),
    absorbFees: !!settings.absorbFees,
    salesStart: settings.salesStart || null,
    salesEnd: settings.salesEnd || null,
    maxTicketsPerBooking: Math.max(1, Math.floor(Number(settings.maxTicketsPerBooking || 10))),
    showRemainingCount: settings.showRemainingCount !== false,
    requireTermsAcceptance: settings.requireTermsAcceptance !== false,
    cancellationNote: settings.cancellationNote || "",
  };
}

function calculateFeeSummary(subtotal, settings) {
  const gst = settings.absorbFees ? 0 : Math.round(subtotal * (settings.gstPercent / 100));
  const convenienceFee = settings.absorbFees ? 0 : Math.round(subtotal * (settings.convenienceFeePercent / 100));
  const platformFee = settings.absorbFees || subtotal <= 0 ? 0 : Math.round(settings.platformFeeFlat);
  return {
    gst,
    convenienceFee,
    platformFee,
    total: subtotal + gst + convenienceFee + platformFee,
  };
}

function getSalesStatus(settings, event) {
  const now = Date.now();
  const eventEnd = getLastShowEndDate(event);
  if (eventEnd && eventEnd.getTime() < now) return { open: false, message: "This event has ended. Ticket booking is closed." };
  const start = settings.salesStart ? new Date(settings.salesStart).getTime() : 0;
  const end = settings.salesEnd ? new Date(settings.salesEnd).getTime() : 0;
  if (start && now < start) return { open: false, message: `Ticket sale starts on ${new Date(start).toLocaleString()}.` };
  if (end && now > end) return { open: false, message: "Ticket sale is closed for this event." };
  return { open: true, message: "" };
}

function clampNumber(value, min, max, fallback) {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, num));
}

function firstShowInstance(shows = []) {
  const first = Array.isArray(shows) ? shows[0] : null;
  return { date: first?.date || "", time: Array.isArray(first?.times) ? first.times[0] || "" : "" };
}

function getLastShowEndDate(event) {
  const shows = Array.isArray(event?.schedule?.shows) ? event.schedule.shows : [];
  const durationMin = Number(event?.additionalInfo?.durationMin || event?.durationMin || 0);
  let last = null;
  for (const show of shows) {
    if (!show?.date) continue;
    const times = Array.isArray(show.times) && show.times.length ? show.times : ["23:59"];
    for (const time of times) {
      const start = parseDateTime(show.date, time || "23:59");
      if (!start) continue;
      const end = new Date(start.getTime() + Math.max(durationMin, 0) * 60 * 1000);
      if (!last || end > last) last = end;
    }
  }
  return last;
}

function parseDateTime(dateISO, hhmm) {
  const t = (hhmm || "00:00").slice(0, 5);
  const date = new Date(`${dateISO}T${t}:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function ticketAvailability(ticket, bookings = []) {
  const total = Number(ticket.qty || 0);
  const sold = bookings.reduce((sum, booking) => {
    const status = String(booking.status || "").toLowerCase();
    if (!["confirmed", "paid", "success"].includes(status)) return sum;
    return sum + (Array.isArray(booking.items) ? booking.items : []).reduce((itemSum, item) => {
      return item.ticket_id === ticket.id ? itemSum + Number(item.quantity || 0) : itemSum;
    }, 0);
  }, 0);
  return { total, sold, available: Math.max(0, total - sold) };
}

function availabilityLine(available) {
  if (available <= 0) return { color: "#b91c1c", fontSize: 12, fontWeight: 900, marginTop: 5 };
  if (available <= 5) return { color: "#b45309", fontSize: 12, fontWeight: 900, marginTop: 5 };
  return { color: COLORS.subtext, fontSize: 12, fontWeight: 800, marginTop: 5 };
}

function pickImage(event) {
  return event?.images?.horizontalBanner?.url || event?.images?.verticalPoster?.url || "";
}

function formatMoney(value) {
  return `Rs ${Number(value || 0).toLocaleString("en-IN")}`;
}

function ensureRazorpayLoaded() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.getElementById("razorpay-checkout-js");
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => reject(new Error("Razorpay checkout failed to load.")));
      return;
    }
    const script = document.createElement("script");
    script.id = "razorpay-checkout-js";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Razorpay checkout failed to load."));
    document.body.appendChild(script);
  });
}

function openRazorpayCheckout(options) {
  return new Promise((resolve, reject) => {
    const checkout = new window.Razorpay({
      ...options,
      theme: { color: COLORS.primary },
      handler: (response) => resolve(response),
      modal: {
        ondismiss: () => reject(new Error("Payment was cancelled.")),
      },
    });
    checkout.on("payment.failed", (response) => {
      reject(new Error(response?.error?.description || "Payment failed."));
    });
    checkout.open();
  });
}

const page = { minHeight: "100vh", background: COLORS.page, padding: 16, color: COLORS.text };
const hero = { maxWidth: 1100, margin: "0 auto 16px", borderRadius: 18, overflow: "hidden", background: COLORS.card, border: `1px solid ${COLORS.border}`, display: "grid", gridTemplateColumns: "minmax(260px, 420px) 1fr" };
const heroImg = { width: "100%", height: "100%", minHeight: 260, objectFit: "cover" };
const heroContent = { padding: 20, display: "grid", alignContent: "center", gap: 8 };
const eyebrow = { color: COLORS.primary, fontWeight: 900, fontSize: 13, textTransform: "uppercase" };
const title = { margin: 0, fontSize: 30, fontWeight: 900 };
const meta = { color: COLORS.subtext, fontSize: 14 };
const promoterChip = { display: "inline-flex", width: "fit-content", borderRadius: 999, padding: "6px 10px", background: "#fff1f7", color: "#be185d", border: "1px solid #f9a8d4", fontWeight: 800, fontSize: 12 };
const grid = { maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr 320px", gap: 14, alignItems: "start" };
const card = { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: 16, display: "grid", gap: 12 };
const sectionTitle = { margin: 0, fontSize: 18, fontWeight: 900 };
const ticketCard = { display: "grid", gridTemplateColumns: "1fr 90px", gap: 10, alignItems: "center", border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 12 };
const qtyInput = { height: 42, borderRadius: 10, border: `1px solid ${COLORS.border}`, padding: "0 10px", fontSize: 16 };
const field = { display: "grid", gap: 6, color: COLORS.subtext, fontSize: 13, fontWeight: 800 };
const input = { height: 44, borderRadius: 10, border: `1px solid ${COLORS.border}`, padding: "0 12px", color: COLORS.text };
const termsLine = { display: "flex", gap: 8, alignItems: "center", color: COLORS.subtext, fontSize: 13 };
const summaryRow = { display: "flex", justifyContent: "space-between", gap: 12, borderBottom: `1px solid ${COLORS.border}`, padding: "8px 0", color: COLORS.text };
const payBtn = { minHeight: 48, border: "none", borderRadius: 12, background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`, color: "#fff", fontWeight: 900, cursor: "pointer", padding: "0 16px" };
const hint = { color: COLORS.subtext, fontSize: 12, lineHeight: 1.5 };
const noteBox = { maxWidth: 1100, margin: "0 auto", background: "#fff", border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 16, color: COLORS.subtext };
const errorBox = { maxWidth: 1100, margin: "0 auto 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 14, padding: 16, color: "#b91c1c", fontWeight: 800 };
const successCard = { maxWidth: 520, margin: "32px auto", background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 18, padding: 22, display: "grid", justifyItems: "center", gap: 12, textAlign: "center" };
const successBadge = { borderRadius: 999, padding: "6px 12px", background: "#ecfdf5", color: "#166534", border: "1px solid #bbf7d0", fontWeight: 900 };
const qrImg = { width: 220, height: 220, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 8, background: "#fff" };
const ticketId = { fontSize: 24, fontWeight: 900, letterSpacing: 1 };
const summaryBox = { width: "100%", border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 12 };
